import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { articlesApi } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, List, Grid3X3,
  PenLine, Clock, Globe, FileText, BookOpen, Wrench, Lightbulb, Loader2
} from "lucide-react";

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAYS_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const STATUS_CONFIG = {
  draft: { label: "Borrador", color: "#FBBF24", bg: "rgba(251,191,36,0.1)" },
  published: { label: "Publicado", color: "#4ADE80", bg: "rgba(74,222,128,0.1)" },
  scheduled: { label: "Programado", color: "#818CF8", bg: "rgba(129,140,248,0.1)" },
};

const CLS_CONFIG = {
  informativo: { icon: BookOpen, color: "#3B82F6" },
  practico: { icon: Wrench, color: "#10B981" },
  aplicable: { icon: Lightbulb, color: "#F0A050" },
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Monday=0
}

function ArticleDot({ article }) {
  const st = STATUS_CONFIG[article.status] || STATUS_CONFIG.draft;
  return (
    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: st.color }} title={article.title} />
  );
}

function CalendarGrid({ year, month, articles, onDayClick }) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  const articlesByDay = {};
  articles.forEach((a) => {
    const dateStr = a.scheduled_date || a.created_at;
    if (!dateStr) return;
    const d = new Date(dateStr);
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      if (!articlesByDay[day]) articlesByDay[day] = [];
      articlesByDay[day].push(a);
    }
  });

  const cells = [];
  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} className="min-h-[90px] rounded-lg" style={{ background: "rgba(11,22,34,0.3)" }} />);
  }
  // Day cells
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday = isCurrentMonth && today.getDate() === day;
    const dayArticles = articlesByDay[day] || [];
    cells.push(
      <div key={day} data-testid={`calendar-day-${day}`}
        onClick={() => onDayClick(year, month, day)}
        className="min-h-[90px] rounded-lg border p-1.5 cursor-pointer transition-all duration-200 hover:border-cyan-500/30"
        style={{
          background: isToday ? "rgba(0,212,255,0.04)" : "#0F2035",
          borderColor: isToday ? "rgba(0,212,255,0.3)" : "#1E3A5F",
        }}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium" style={{
            color: isToday ? "#00D4FF" : "#94A3B8",
            fontFamily: "'IBM Plex Mono', monospace",
          }}>
            {day}
          </span>
          {dayArticles.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "rgba(0,212,255,0.08)", color: "#00D4FF", fontSize: "10px" }}>
              {dayArticles.length}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {dayArticles.slice(0, 3).map((a) => (
            <ArticleDot key={a.id} article={a} />
          ))}
          {dayArticles.length > 3 && (
            <span className="text-xs" style={{ color: "#64748B", fontSize: "9px" }}>+{dayArticles.length - 3}</span>
          )}
        </div>
        {dayArticles.length > 0 && (
          <div className="mt-1">
            {dayArticles.slice(0, 2).map((a) => (
              <div key={a.id} className="text-xs truncate leading-tight" style={{ color: "#CBD5E1", fontSize: "10px" }}>
                {a.title}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS_ES.map((d) => (
          <div key={d} className="text-center text-xs py-2 font-medium" style={{ color: "#64748B", fontFamily: "'IBM Plex Mono', monospace" }}>
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells}
      </div>
    </div>
  );
}

function ListView({ articles, navigate }) {
  if (articles.length === 0) {
    return (
      <div className="text-center py-12">
        <CalendarIcon className="w-10 h-10 mx-auto mb-3" style={{ color: "#94A3B8" }} />
        <p className="text-sm" style={{ color: "#94A3B8" }}>No hay artículos este mes</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {articles.map((a) => {
        const st = STATUS_CONFIG[a.status] || STATUS_CONFIG.draft;
        const cls = CLS_CONFIG[a.classification] || CLS_CONFIG.informativo;
        const ClsIcon = cls.icon;
        const dateStr = a.scheduled_date || a.created_at;
        const d = new Date(dateStr);
        const dateFormatted = d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
        const timeFormatted = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

        return (
          <div key={a.id} data-testid={`list-article-${a.id}`}
            onClick={() => navigate(`/articles/${a.id}`)}
            className="flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:border-cyan-500/20"
            style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
            <div className="flex-shrink-0 w-12 text-center">
              <div className="text-lg font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{d.getDate()}</div>
              <div className="text-xs uppercase" style={{ color: "#64748B", fontFamily: "'IBM Plex Mono'" }}>{d.toLocaleDateString("es-ES", { month: "short" })}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: "#E2E8F0", fontFamily: "'Outfit'" }}>{a.title}</div>
              <div className="flex items-center gap-2 mt-1">
                <Badge className="text-xs px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color, border: `1px solid ${st.color}33` }}>
                  {st.label}
                </Badge>
                <span className="flex items-center gap-1 text-xs" style={{ color: "#64748B" }}>
                  <ClsIcon className="w-3 h-3" style={{ color: cls.color }} />
                  {a.classification}
                </span>
                <span className="flex items-center gap-1 text-xs" style={{ color: "#64748B" }}>
                  <Clock className="w-3 h-3" /> {timeFormatted}
                </span>
                {a.wp_post_id && (
                  <span className="flex items-center gap-1 text-xs" style={{ color: "#21759b" }}>
                    <Globe className="w-3 h-3" /> WP
                  </span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 text-xs" style={{ color: "#64748B", fontFamily: "'IBM Plex Mono'" }}>
              {a.reading_time || 0} min
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function PublicationCalendar() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [view, setView] = useState("calendar"); // "calendar" | "list"
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());

  useEffect(() => { loadArticles(); }, [year, month]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const res = await articlesApi.calendar(year, month + 1);
      setArticles(res.data);
    } catch {
      toast.error("Error al cargar artículos");
    } finally { setLoading(false); }
  };

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const onDayClick = (y, m, d) => {
    // Check if there are articles on this day
    const dayArticles = articles.filter((a) => {
      const dateStr = a.scheduled_date || a.created_at;
      if (!dateStr) return false;
      const dt = new Date(dateStr);
      return dt.getFullYear() === y && dt.getMonth() === m && dt.getDate() === d;
    });
    if (dayArticles.length === 1) {
      navigate(`/articles/${dayArticles[0].id}`);
    } else if (dayArticles.length > 1) {
      toast.info(`${dayArticles.length} artículos el día ${d}. Usa la vista lista para verlos.`);
    } else {
      navigate("/new");
    }
  };

  // Stats for the month — based on ALL articles (incl. drafts)
  const totalArticles = articles.length;
  const publishedCount = articles.filter((a) => a.status === "published").length;
  const scheduledCount = articles.filter((a) => a.status === "scheduled").length;
  const draftCount = articles.filter((a) => a.status === "draft").length;

  // Articles shown on the calendar grid — drafts excluded so they don't appear on creation day
  const calendarArticles = articles.filter((a) => a.status !== "draft");
  const sortedCalendarArticles = [...calendarArticles].sort((a, b) => {
    const da = a.scheduled_date || a.created_at;
    const db2 = b.scheduled_date || b.created_at;
    return da > db2 ? 1 : -1;
  });

  return (
    <div data-testid="calendar-page" className="min-h-screen" style={{ background: "#0B1622" }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,212,255,0.1)" }}>
              <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: "#00D4FF" }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>Calendario</h1>
              <p className="text-sm" style={{ color: "#94A3B8" }}>Planifica y visualiza tus publicaciones</p>
            </div>
          </div>
          <Button data-testid="calendar-new-article-btn" onClick={() => navigate("/new")}
            className="rounded-full px-5 text-sm font-medium"
            style={{ background: "linear-gradient(135deg, #00B4D8, #00D4FF)", color: "#0B1622" }}>
            <PenLine className="w-4 h-4 mr-1.5" />Nuevo
          </Button>
        </div>

        {/* Month Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
          {[
            { label: "Total", value: totalArticles, color: "#00D4FF", icon: FileText },
            { label: "Publicados", value: publishedCount, color: "#4ADE80", icon: Globe },
            { label: "Programados", value: scheduledCount, color: "#818CF8", icon: Clock },
            { label: "Borradores", value: draftCount, color: "#FBBF24", icon: PenLine },
          ].map((s) => (
            <div key={s.label} className="rounded-xl p-3 border" style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <div className="flex items-center gap-1.5 mb-1">
                <s.icon className="w-3.5 h-3.5" style={{ color: s.color }} />
                <span className="text-xs uppercase tracking-wider" style={{ color: "#64748B", fontFamily: "'IBM Plex Mono'" }}>{s.label}</span>
              </div>
              <div className="text-xl font-bold" style={{ color: "#E2E8F0", fontFamily: "'Space Grotesk'" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <button data-testid="prev-month-btn" onClick={prevMonth}
              className="w-9 h-9 rounded-lg flex items-center justify-center border transition-colors hover:border-cyan-500/30"
              style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <ChevronLeft className="w-4 h-4" style={{ color: "#94A3B8" }} />
            </button>
            <h2 className="text-lg font-semibold min-w-[200px] text-center" style={{ fontFamily: "'Space Grotesk'", color: "#E2E8F0" }}>
              {MONTHS_ES[month]} {year}
            </h2>
            <button data-testid="next-month-btn" onClick={nextMonth}
              className="w-9 h-9 rounded-lg flex items-center justify-center border transition-colors hover:border-cyan-500/30"
              style={{ background: "#0F2035", borderColor: "#1E3A5F" }}>
              <ChevronRight className="w-4 h-4" style={{ color: "#94A3B8" }} />
            </button>
            <button data-testid="go-today-btn" onClick={goToday}
              className="text-xs px-3 py-1.5 rounded-full border transition-colors"
              style={{ borderColor: "#1E3A5F", color: "#94A3B8" }}>
              Hoy
            </button>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: "#0F2035" }}>
            <button data-testid="view-calendar-btn" onClick={() => setView("calendar")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-all"
              style={{ background: view === "calendar" ? "rgba(0,212,255,0.1)" : "transparent", color: view === "calendar" ? "#00D4FF" : "#94A3B8" }}>
              <Grid3X3 className="w-3.5 h-3.5" /> Calendario
            </button>
            <button data-testid="view-list-btn" onClick={() => setView("list")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-all"
              style={{ background: view === "list" ? "rgba(0,212,255,0.1)" : "transparent", color: view === "list" ? "#00D4FF" : "#94A3B8" }}>
              <List className="w-3.5 h-3.5" /> Lista
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin mr-2" style={{ color: "#00D4FF" }} />
            <span className="text-sm" style={{ color: "#94A3B8" }}>Cargando...</span>
          </div>
        ) : view === "calendar" ? (
          <div className="rounded-xl border p-4" style={{ background: "#0B1622", borderColor: "#1E3A5F" }}>
            <CalendarGrid year={year} month={month} articles={articles} onDayClick={onDayClick} />
          </div>
        ) : (
          <ListView articles={sortedCalendarArticles} navigate={navigate} />
        )}

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
              <span className="text-xs" style={{ color: "#64748B" }}>{cfg.label}</span>
            </div>
          ))}
          <span className="text-xs ml-auto" style={{ color: "#475569" }}>
            Clic en un dia vacio para crear articulo
          </span>
        </div>
      </main>
    </div>
  );
}
